/**
 * "EXPLAIN THIS" AS AGED CARE TELLS IT, FOR EVERY MODULE (owner, 22 Oct 2026: "the UI for the explain this does not
 * match the updates that were carried out for aged care … all modules use consistent UI").
 *
 * Numbered lines — each says what is done and what that does to the numbers — then the "=" line: what is left to the
 * family after the horizon, against the benchmark. The navy numerals, hairlines and heavy rule are aged care's
 * (StrategyPlanRecap told="causeEffect") and support at home's (SahCauseEffect). The money story (which account pays
 * each year's shortfall, and when it runs low) belongs inside the "Put together" line, passed in by the caller.
 * Anything after the ledger (the pieces of the plan, the family panel, debts) is passed as children.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { LeverExplainers } from './LeverExplainers.tsx';
import { comparedOverSentence } from '../../features/advice/comparedOver.ts';

export interface CauseEffectLedgerProps {
    lines: React.ReactNode[];
    /** What is left to the family at the horizon, and the benchmark's figure. */
    estate: number;
    baseEstate?: number | null;
    horizonYears: number;
    /** "doing nothing", "staying put", "changing nothing", "the best way to move". */
    baseWord: string;
    testId?: string;
    children?: React.ReactNode;
    /** The plan's pieces (the kinds "What could go wrong" reads): what each is and how it works, above the lines. */
    leverKinds?: ReadonlyArray<string>;
}

const money = (n: number) => formatCurrency(Math.abs(Math.round(n)));

/*
 * THE COLOUR TOUCHES (owner-approved mock-up v3, 23 Oct 2026), shared so every module's Explain this matches:
 * the step numbers in the brand gradient, each "from → to" figure in navy with the change marked green when it
 * helps the family and red when it costs, and the answer as a large figure with its difference in a small tag.
 */

/** A step's number: a small circle in the brand gradient (IconTile's). */
export const StepNumber = ({ n }: { n: number }) => (
    <span className="mt-0.5 grid h-7 w-7 place-items-center rounded-full bg-brand-navy text-[13px] font-bold tabular-nums text-white" data-testid="ledger-step-number">{n}</span>
);

/** The "=" that opens the answer, unchanged. */
export const ResultMarker = () => <span className="pt-0.5 text-[20px] font-bold text-brand-navy">=</span>;

/**
 * "from $X to $Y a year ▲ $Z": the two figures in navy, the unit as the sentence has it, then the change marked.
 * `better` says which way helps the family, decided where the line is built from what the figure is (a cost or a
 * shortfall falling helps; a pension, an income or an estate rising helps). Without it no mark is shown.
 */
export interface FigureChange {
    from: number;
    to: number;
    /** What follows the second figure in the sentence: " a year", " a day", " in the first year". */
    unit?: string;
    better?: 'up' | 'down' | null;
}
export const FromTo = ({ from, to, unit = '', better = null }: FigureChange) => {
    const diff = to - from;
    const marked = better != null && Math.abs(diff) >= 1;
    const good = better === 'up' ? diff > 0 : diff < 0;
    return (
        <>from <span className="font-semibold tabular-nums text-brand-navy">{money(from)}</span> to <span className="font-semibold tabular-nums text-brand-navy">{money(to)}</span>{unit}
            {marked && <> <span className={`whitespace-nowrap font-bold tabular-nums ${good ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="ledger-change">{diff < 0 ? '▼' : '▲'} {money(diff)}</span></>}
        </>
    );
};

/**
 * A line's figure as data, for builders that write their lines as plain text (the advice document reads the text):
 * the screen shows `<FromTo>` in place of the "from … to …" words, then `after` (the rest of the sentence).
 */
export interface LedgerChange extends FigureChange {
    after: string;
}

/** The figure part of a line: the structured change where there is one, else the line's own words. */
export const ChangeOrText = ({ change, text }: { change?: LedgerChange | null; text: React.ReactNode }) => (
    change ? <> <FromTo from={change.from} to={change.to} unit={change.unit} better={change.better} />{change.after}</> : <>{text}</>
);

/**
 * The answer under the "=": "After about N years the family is left", the amount large, the difference in a small
 * tag, then whatever the module says after it.
 */
export const LedgerResult = ({ horizonYears, estate, baseEstate = null, baseWord, children }: { horizonYears: number; estate: number; baseEstate?: number | null; baseWord: string; children?: React.ReactNode }) => {
    const d = baseEstate != null ? estate - baseEstate : null;
    return (
        <div>
            <p className="m-0 text-[15px] text-slate-600">After about {horizonYears} {horizonYears === 1 ? 'year' : 'years'} the family is left</p>
            <p className="m-0 text-[30px] font-extrabold leading-tight tabular-nums text-brand-navy" data-testid="ledger-result-amount">{money(estate)}</p>
            {d != null && Math.abs(d) >= 1 && (
                <span className={`mt-1.5 inline-block rounded-full px-3 py-0.5 text-[13.5px] font-bold ${d >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="ledger-result-tag">{money(d)} {d >= 0 ? 'more' : 'less'} than {baseWord}</span>
            )}
            {d != null && Math.abs(d) < 1 && <span className="mt-1.5 inline-block rounded-full bg-slate-100 px-3 py-0.5 text-[13.5px] font-bold text-slate-700" data-testid="ledger-result-tag">the same as {baseWord}</span>}
            {children && <div className="mt-2 text-[15px] leading-relaxed text-slate-700">{children}</div>}
            {/* Every module's Explain this ends its answer the same way (owner, 25 Oct 2026). */}
            <p className="mt-2 text-[13px] leading-relaxed text-slate-500" data-testid="ledger-compared-over">{comparedOverSentence(horizonYears)}</p>
        </div>
    );
};

export const CauseEffectLedger = ({ lines, estate, baseEstate = null, horizonYears, baseWord, testId = 'cause-effect', children, leverKinds = [] }: CauseEffectLedgerProps) => {
    return (
        <div data-testid={testId}>
            {/* What each piece IS, before what it does (owner, 24 Oct 2026: "it doesnt actually explain what it is, how it works"). */}
            <LeverExplainers kinds={leverKinds} className="mb-5" />
            <ol className="m-0 list-none p-0">
                {lines.map((l, i) => (
                    <li key={i} className={`grid grid-cols-[2.25rem_1fr] gap-x-2 border-b border-slate-200 py-4 text-[15px] leading-relaxed text-slate-700 ${i === 0 ? 'pt-0' : ''}`}>
                        <StepNumber n={i + 1} />
                        <div>{l}</div>
                    </li>
                ))}
                <li className="grid grid-cols-[2.25rem_1fr] gap-x-2 border-t-2 border-brand-navy py-4 text-[15px] leading-relaxed text-slate-700" data-testid={`${testId}-result`}>
                    <ResultMarker />
                    <LedgerResult horizonYears={horizonYears} estate={estate} baseEstate={baseEstate} baseWord={baseWord} />
                </li>
            </ol>
            {children}
            <p className="mt-5 text-[12.5px] leading-relaxed text-slate-500">Projections depend on the assumptions shown — returns, inflation and today&apos;s rules — and are illustrations, not guarantees.</p>
        </div>
    );
};

/** The "Put together" line every ledger closes its numbered lines with, with the money story inside it. */
export function putTogetherLine(i: { cashflow: number; baseCashflow?: number | null; they: string; baseWord: string; moneyStory?: React.ReactNode }): React.ReactNode {
    const base = i.baseCashflow != null ? ` (${i.baseWord}, ${i.baseCashflow < 0 ? `${money(i.baseCashflow)} short` : `${money(i.baseCashflow)} to spare`})` : '';
    return i.cashflow >= 0
        ? <><strong className="text-slate-900">Put together, {i.they} have {money(i.cashflow)} a year to spare</strong>{base}.{i.moneyStory ? <> {i.moneyStory}</> : null}</>
        : <><strong className="text-slate-900">Put together, {i.they} are {money(i.cashflow)} a year short</strong>, met from savings{base}.{i.moneyStory ? <> {i.moneyStory}</> : null}</>;
}

export default CauseEffectLedger;
